import { useApp } from '@/contexts';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Info as InfoIcon, Lock as LockIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  Toolbar as MuiToolbar,
  Skeleton,
  Snackbar,
  Typography,
} from '@mui/material';
import { ReactFlowProvider } from '@xyflow/react';
import { useParams, useSearchParams } from 'react-router';

import {
  useGetConfigTemplatesQuery,
  useGetEnvironmentsQuery,
} from '@/features/environments/api/environmentsApi';
import { useGetTenantQuery } from '@/features/tenants/api/tenantsApi';
import {
  useDeployTransformerMutation,
  useGetTransformerDeploymentsQuery,
  useGetTransformerVersionsQuery,
} from '@/features/transformers/api/transformersApi';
import { useGetCurrentUserQuery } from '@/features/users/api/usersApi';
import type { Recipe } from '@/types';
import { logger } from '@/utils/logger';

import FlowWithProvider from '../Flow';
import { useGetRecipeContentQuery, useSaveAsNewVersionMutation } from '../api/recipeEditorApi';
import { RecipeEditorProvider, useRecipeEditor } from '../context/RecipeEditorContext';
import {
  clearPostSaveRefetch,
  emitRecipeLoaded,
  isPostSaveRefetch,
  setRecipeLoading,
} from '../events/recipeEditorEvents';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useRecipeEditorEvents } from '../hooks/useRecipeEditorEvents';
import { useToolbarActions } from '../hooks/useToolbarActions';
import { recipeToNodes, recipeToYaml } from '../utils/recipeConverter';
import { BottomPanel } from './BottomPanel';
import { CommandPalette, useCommandPaletteCommands } from './CommandPalette';
import { CompareView } from './CompareView';
import { RecipeEditorToolbar } from './RecipeEditorToolbar';
import RecipeListSidebar from './RecipeListSidebar';
import { YamlEditor } from './YamlEditor';
import { CheckInDialog } from './modals/CheckInDialog';
import { DeployDialog } from './modals/DeployDialog';
import { MetadataDialog } from './modals/MetadataDialog';
import { SaveConflictDialog } from './modals/SaveConflictDialog';
import { ScheduleDialog } from './modals/ScheduleDialog';
import { SqlEditorDialog } from './modals/SqlEditorDialog';
import { TemplateDialog } from './modals/TemplateDialog';
import { UnsavedChangesDialog } from './modals/UnsavedChangesDialog';
import { VersionsDialog } from './modals/VersionsDialog';

const EMPTY_TEMPLATES: unknown[] = [];

interface RecipeEditorContentProps {
  recipeId?: string;
}

/**
 * Recipe Editor Content - the actual editor UI with full toolbar
 */
const RecipeEditorContent = ({ recipeId }: RecipeEditorContentProps) => {
  const { appId, tenantId, app } = useApp();

  const { state, dispatch, setRecipe, setNodes, setEdges, setYamlContent, setIsModified } =
    useRecipeEditor();

  // Listen to recipe editor events and update state
  useRecipeEditorEvents();

  const {
    nodes: _nodes,
    edges: _edges,
    isReadOnly,
    editorMode,
    yamlContent,
    showCompare,
    bottomPanelTabs,
    showVersionsDialog,
    showCheckInDialog,
    showDeployDialog,
    showScheduleDialog,
    showTemplateDialog,
    showMetadataDialog,
    showSqlEditorDialog,
    showSaveConflictDialog,
    showUnsavedChangesDialog,
    showCommandPalette,
    selectedEnvironment,
    currentVersion,
    versions,
    selectedSqlQuery,
  } = state;

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Monaco editor ref — stored here so we can push content imperatively
  // without going through the React render cycle (which causes cursor jumps).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Monaco editor type
  const yamlEditorRef = useRef<any>(null);

  // Synchronous guard: true while pushYamlToEditor is calling editor.setValue().
  // Monaco fires onChange synchronously during setValue(), so handleYamlChange
  // can check this ref to distinguish imperative pushes from user edits.
  const isPushingToEditorRef = useRef(false);

  // Push YAML content to both context state AND Monaco editor imperatively.
  // This must be used for ALL external content changes (recipe load, version
  // switch, discard, save response). User typing goes through handleYamlChange
  // which only updates context state — Monaco already has the user's edits.
  const pushYamlToEditor = useCallback(
    (content: string) => {
      setYamlContent(content);
      if (yamlEditorRef.current) {
        const model = yamlEditorRef.current.getModel();
        if (model && model.getValue() !== content) {
          isPushingToEditorRef.current = true;
          yamlEditorRef.current.setValue(content);
          isPushingToEditorRef.current = false;
        }
      }
    },
    [setYamlContent]
  );

  // API hooks - Current user (for role check)
  const { data: currentUser } = useGetCurrentUserQuery();

  // API hooks - Templates (conditional on user role)
  const { data: templates = EMPTY_TEMPLATES as { name: string }[] } = useGetConfigTemplatesQuery(
    undefined,
    {
      skip: !currentUser || currentUser.role !== 'tech-support',
    }
  );

  // API hooks - Recipe
  const {
    data: recipeContent,
    isLoading: isLoadingRecipe,
    isFetching: isFetchingRecipe,
    error: loadError,
    refetch,
  } = useGetRecipeContentQuery(
    { baseId: recipeId!, appId: appId! },
    {
      skip: !recipeId || recipeId === 'new' || recipeId === 'undefined' || !appId,
      refetchOnMountOrArgChange: true, // Always refetch, never serve stale cache
    }
  );

  // API hooks - Versions
  const { data: versionsData, isLoading: isLoadingVersions } = useGetTransformerVersionsQuery(
    { transBaseId: recipeId!, appId: appId! },
    { skip: !recipeId || !appId || recipeId === 'new' || recipeId === 'undefined' }
  );

  // API hooks - Deployments (for per-environment deploy check)
  const { data: deployments } = useGetTransformerDeploymentsQuery(
    { appId: appId!, baseId: recipeId! },
    { skip: !recipeId || !appId || recipeId === 'new' || recipeId === 'undefined' }
  );

  // API hooks - Environments
  const { data: environmentsData } = useGetEnvironmentsQuery(
    { tenantId, appId: appId! },
    { skip: !appId || !tenantId }
  );

  // API hooks - Tenant (for tenantKey needed in execution event context)
  const { data: tenantData } = useGetTenantQuery(tenantId, { skip: !tenantId });

  const [saveAsNewVersion] = useSaveAsNewVersionMutation();
  const [deployTransformer] = useDeployTransformerMutation();

  // Prepare environment list for toolbar + execution context.
  // Cross-references deployments and versions to compute the deployed version
  // number per environment (mirrors legacy EnvironmentDropdown.js behavior).
  const environmentsList = useMemo(() => {
    return (environmentsData || []).map((env) => {
      let deployedVersion: number | undefined;
      if (deployments && versionsData) {
        const deployment = deployments.find((d) => d.environmentId === env.id);
        if (deployment) {
          const deployedVer = versionsData.find((v) => v.id === deployment.transId);
          if (deployedVer) {
            deployedVersion = parseInt(String(deployedVer.version), 10) || undefined;
          }
        }
      }
      return {
        id: env.id,
        name: env.name || env.id,
        color: env.color,
        deployedVersion,
      };
    });
  }, [environmentsData, deployments, versionsData]);

  // Check if the current version is already deployed to the selected environment.
  // Mirrors legacy selectedEnvironmentIsDeployedToCurVer() which prevents duplicate deploys.
  const isDeployedToSelectedEnv = useMemo(() => {
    if (!deployments || !selectedEnvironment || !versionsData) return false;
    const currentVer = versionsData.find((v) => String(v.version) === String(currentVersion));
    if (!currentVer) return false;
    return deployments.some(
      (d) => d.transId === currentVer.id && d.environmentId === selectedEnvironment
    );
  }, [deployments, selectedEnvironment, versionsData, currentVersion]);

  // Authoritative deployment check: is the current version deployed to ANY environment?
  // Legacy dataProtect's versionIsDeployed() checks transformer.deployments (from the
  // deployments API) rather than the Deployed boolean on the version object, which the
  // backend may not reliably set.
  const isCurrentVersionDeployed = useMemo(() => {
    if (!deployments || !versionsData) return false;
    const currentVer = versionsData.find((v) => String(v.version) === String(currentVersion));
    if (!currentVer) return false;
    return deployments.some((d) => d.transId === currentVer.id);
  }, [deployments, versionsData, currentVersion]);

  // Toolbar actions hook with error/success callbacks
  const toolbarActions = useToolbarActions({
    onError: (message) => {
      setSnackbar({ open: true, message, severity: 'error' });
    },
    onSuccess: (message) => {
      setSnackbar({ open: true, message, severity: 'success' });
    },
    // Pass required functions for mode switching
    refetch,
    setRecipe,
    setNodes,
    setEdges,
    setYamlContent: pushYamlToEditor,
    recipeToNodes,
    // Execution context - needed for legacy-compatible event context POST
    environments: environmentsList,
    tenantKey: tenantData?.key || '',
    appUniqueKey: app.uniqueKey || '',
  });

  // ========================================
  // Recipe Switch Detection
  // ========================================
  // When recipeId changes (user clicked a recipe in sidebar, or URL navigation),
  // ensure the switching overlay is visible. The overlay hides the (potentially stale)
  // editor content while we wait for RTK Query to return the new recipe data.
  //
  // We intentionally do NOT clear yamlContent/recipe here. Doing so would trigger
  // an intermediate Monaco editor.setValue('') whose onChange fires and poisons the
  // sync ref, causing the REAL content push to be skipped. Instead we rely on:
  //   1. The overlay to visually hide stale content.
  //   2. The YamlEditor being keyed on recipeId so it remounts with a clean slate.
  //   3. The recipe-load effect below to write all the correct state when data arrives.
  const prevRecipeIdRef = useRef(recipeId);

  useEffect(() => {
    if (recipeId && recipeId !== prevRecipeIdRef.current) {
      prevRecipeIdRef.current = recipeId;
      // Reset so the recipe loading effect processes the new recipe's data
      lastProcessedRecipeContentRef.current = undefined;
      // Always show the switching overlay (catches URL navigation without sidebar click)
      dispatch({ type: 'SET_IS_SWITCHING_RECIPE', payload: true });
      // Prevent false modification events from ReactFlow/Monaco during the transition
      setRecipeLoading(true);
    }
  }, [recipeId, dispatch]);

  // Clear switching state on load error so user isn't stuck with overlay
  useEffect(() => {
    if (loadError && state.isSwitchingRecipe) {
      dispatch({ type: 'SET_IS_SWITCHING_RECIPE', payload: false });
      setRecipeLoading(false);
    }
  }, [loadError, state.isSwitchingRecipe, dispatch]);

  // Track which recipeContent object we last processed, so we don't re-run the
  // entire load pipeline when unrelated deps (like isCurrentVersionDeployed) change.
  const lastProcessedRecipeContentRef = useRef<typeof recipeContent>(undefined);

  // Load recipe data when content is fetched.
  //
  // IMPORTANT: We only write editor content when `isFetchingRecipe` is false,
  // meaning the data is final (not a stale cache being served during a
  // background refetch). Without this guard, save-triggered tag invalidation
  // causes the load effect to re-run with STALE cached data while the refetch
  // is in progress, briefly flashing old content before the fresh data arrives.
  //
  // Timeline without guard:
  //   Save → pushYamlToEditor(newContent) → tag invalidation → refetch starts →
  //   isFetching=true + stale recipeContent → effect pushes OLD content (flash!) →
  //   refetch completes → effect pushes new content
  //
  // Timeline with guard:
  //   Save → pushYamlToEditor(newContent) → tag invalidation → refetch starts →
  //   isFetching=true → effect skips content push (editor keeps new content) →
  //   refetch completes → isFetching=false → effect pushes fresh data (no flash)
  useEffect(() => {
    // Skip content push while RTK Query is refetching (serving stale cache).
    if (isFetchingRecipe) {
      return;
    }

    if (recipeContent && (recipeContent.recipe || recipeContent.config)) {
      // Post-save refetch: the editor already has the correct content from the
      // save response (handleSave processed it). Skip the expensive node/edge
      // reconstruction and ReactFlow remount to avoid UI jank. Only update
      // lightweight metadata that might have changed on the server.
      //
      // IMPORTANT: Do NOT call setRecipeLoading() or emitRecipeLoaded() here.
      // emitRecipeLoaded() fires 'recipe:loaded' which dispatches SET_IS_MODIFIED: false,
      // potentially overwriting a user's modification made between save and refetch completion.
      if (isPostSaveRefetch()) {
        clearPostSaveRefetch();

        // Lightweight metadata sync only — no node/edge/YAML rebuild, no isModified reset
        dispatch({ type: 'SET_CURRENT_VERSION', payload: recipeContent.version });
        dispatch({ type: 'SET_VERSION_IS_DEPLOYED', payload: recipeContent.isDeployed });

        dispatch({
          type: 'SET_RECIPE_METADATA',
          payload: {
            versionId: recipeContent.versionId,
            name: recipeContent.name || '',
            config: recipeContent.config || '',
            environ: recipeContent.environ || '',
            description: recipeContent.description || '',
            languageVersion: recipeContent.languageVersion || '2.0',
            processingType: recipeContent.processingType || 'batch',
            maxDataProcTime: recipeContent.maxDataProcTime || '00:00:00',
            templateName: recipeContent.templateName || '',
          },
        });

        dispatch({ type: 'SET_IS_SWITCHING_RECIPE', payload: false });
        lastProcessedRecipeContentRef.current = recipeContent;
        return;
      }

      // Guard: only process when recipeContent actually changed (new API data).
      // Without this, the effect re-runs when unrelated deps change (e.g.
      // isCurrentVersionDeployed after a version switch) and overwrites the
      // user's explicit version selection with the latest version.
      if (lastProcessedRecipeContentRef.current === recipeContent) {
        return;
      }
      lastProcessedRecipeContentRef.current = recipeContent;

      const yamlToSet =
        recipeContent.config ?? (recipeContent.recipe ? recipeToYaml(recipeContent.recipe) : '');

      // Suppress false modification events during state hydration
      setRecipeLoading(true);

      if (recipeContent.recipe) {
        setRecipe(recipeContent.recipe);
        const { nodes: newNodes, edges: newEdges } = recipeToNodes(recipeContent.recipe);
        setNodes(newNodes);
        setEdges(newEdges);
      } else {
        setRecipe({});
        setNodes([]);
        setEdges([]);
      }
      pushYamlToEditor(yamlToSet);
      setIsModified(false);

      dispatch({ type: 'INCREMENT_REACT_FLOW_KEY' });
      dispatch({ type: 'SET_CURRENT_VERSION', payload: recipeContent.version });
      dispatch({
        type: 'SET_VERSION_IS_DEPLOYED',
        payload: recipeContent.isDeployed || isCurrentVersionDeployed,
      });

      if (recipeContent.isDeployed || isCurrentVersionDeployed) {
        dispatch({ type: 'SET_IS_READ_ONLY', payload: true });
        dispatch({ type: 'SET_AUTOSAVE_ENABLED', payload: false });
      }

      dispatch({
        type: 'SET_RECIPE_METADATA',
        payload: {
          versionId: recipeContent.versionId,
          name: recipeContent.name || '',
          config: recipeContent.config || '',
          environ: recipeContent.environ || '',
          description: recipeContent.description || '',
          languageVersion: recipeContent.languageVersion || '2.0',
          processingType: recipeContent.processingType || 'batch',
          maxDataProcTime: recipeContent.maxDataProcTime || '00:00:00',
          templateName: recipeContent.templateName || '',
        },
      });

      emitRecipeLoaded();
      dispatch({ type: 'SET_IS_SWITCHING_RECIPE', payload: false });
    } else if (recipeId === 'new') {
      setRecipeLoading(true);

      setRecipe({});
      setNodes([]);
      setEdges([]);
      pushYamlToEditor(
        '# New Recipe\n# Drag nodes from the sidebar to build your recipe\n\nrecipe:\n  connectors:\n  statements:\n'
      );
      setIsModified(false);

      dispatch({ type: 'INCREMENT_REACT_FLOW_KEY' });
      dispatch({ type: 'SET_RECIPE_METADATA', payload: null });

      emitRecipeLoaded();
      dispatch({ type: 'SET_IS_SWITCHING_RECIPE', payload: false });
    }
  }, [
    recipeContent,
    recipeId,
    isFetchingRecipe,
    isCurrentVersionDeployed,
    setRecipe,
    pushYamlToEditor,
    setNodes,
    setEdges,
    setIsModified,
    dispatch,
  ]);

  // Load versions and enforce read-only for non-latest versions
  useEffect(() => {
    if (versionsData) {
      const sortedVersions = [...versionsData].sort((a, b) => {
        const vA = parseInt(String(a.version), 10) || 0;
        const vB = parseInt(String(b.version), 10) || 0;
        return vB - vA;
      });
      dispatch({ type: 'SET_VERSIONS', payload: sortedVersions });

      const latest = sortedVersions[0];

      if (latest) {
        const isLatest = String(currentVersion) === String(latest.version);
        dispatch({ type: 'SET_IS_LATEST_VERSION', payload: isLatest });

        // Non-latest versions are always read-only (matches legacy dataProtect)
        if (!isLatest) {
          dispatch({ type: 'SET_IS_READ_ONLY', payload: true });
          dispatch({ type: 'SET_AUTOSAVE_ENABLED', payload: false });
        } else if (!isCurrentVersionDeployed) {
          // Only clear read-only if it's the latest AND not deployed
          dispatch({ type: 'SET_IS_READ_ONLY', payload: false });
        }
      }
    }
  }, [versionsData, currentVersion, isCurrentVersionDeployed, dispatch]);

  // Sync deployment status from deployments API (authoritative override).
  // The version object's Deployed field may not be set by the backend, so we
  // rely on the deployments list to determine if the current version is deployed.
  useEffect(() => {
    if (!deployments) return;

    dispatch({ type: 'SET_VERSION_IS_DEPLOYED', payload: isCurrentVersionDeployed });
    if (isCurrentVersionDeployed) {
      dispatch({ type: 'SET_IS_READ_ONLY', payload: true });
      dispatch({ type: 'SET_AUTOSAVE_ENABLED', payload: false });
    } else if (state.isLatestVersion) {
      dispatch({ type: 'SET_IS_READ_ONLY', payload: false });
    }
  }, [isCurrentVersionDeployed, state.isLatestVersion, deployments, dispatch]);

  // Initialize template state based on user role
  useEffect(() => {
    if (currentUser) {
      const isTechSupport = currentUser.role === 'tech-support';
      dispatch({ type: 'SET_SHOW_TEMPLATE_SELECTION', payload: isTechSupport });

      // Load templates if tech-support
      if (isTechSupport && templates.length > 0) {
        dispatch({
          type: 'SET_AVAILABLE_TEMPLATES',
          payload: (templates as { name: string }[]).map((t) => t.name),
        });
      }
    }
  }, [currentUser, templates, dispatch]);

  // Sync selectedTemplate with recipeMetadata for save operations
  useEffect(() => {
    if (
      state.selectedTemplate &&
      state.recipeMetadata &&
      state.recipeMetadata.templateName !== state.selectedTemplate
    ) {
      dispatch({
        type: 'SET_RECIPE_METADATA',
        payload: {
          ...state.recipeMetadata,
          templateName: state.selectedTemplate,
        },
      });
    }
  }, [state.selectedTemplate, state.recipeMetadata, dispatch]);

  // Modal handlers
  const handleCreateVersion = useCallback(
    async (comment: string) => {
      if (!recipeId || !appId) return;

      try {
        const metadata = state.recipeMetadata;

        // Calculate the new version number (legacy: transformer.versions.length + 1)
        const newVersionNumber = (versionsData?.length || 0) + 1;

        // Build content based on editor mode
        const isVisual = editorMode === 'visual';
        const updatedRecipe = JSON.parse(
          sessionStorage.getItem('UPDATED_RECIPE') || JSON.stringify(state.recipe || {})
        );
        const configJson = isVisual ? JSON.stringify({ recipe: updatedRecipe }) : undefined;
        const config = isVisual ? recipeToYaml(updatedRecipe) : undefined;

        await saveAsNewVersion({
          baseId: recipeId,
          appId,
          editorMode,
          configJson,
          yamlContent: isVisual ? undefined : yamlContent,
          config: config || metadata?.config || '',
          comment,
          newVersion: newVersionNumber,
          // Pass all metadata (matches legacy edittrans.js callNewVersionTransformer)
          name: metadata?.name || '',
          environ: metadata?.environ || '',
          description: metadata?.description || '',
          languageVersion: metadata?.languageVersion || '2.0',
          processingType: metadata?.processingType || 'batch',
          maxDataProcTime: metadata?.maxDataProcTime || '00:00:00',
          templateName: metadata?.templateName || '',
        }).unwrap();

        // CRITICAL: Explicitly set version state BEFORE refetch effects run.
        // The new version is never deployed, so clear read-only immediately.
        // Without this, the versions useEffect may see stale versionsData and
        // re-set isReadOnly=true because it thinks we're on an old version.
        dispatch({ type: 'SET_VERSION_IS_DEPLOYED', payload: false });
        dispatch({ type: 'SET_IS_READ_ONLY', payload: false });
        dispatch({ type: 'SET_IS_LATEST_VERSION', payload: true });
        dispatch({ type: 'SET_CURRENT_VERSION', payload: newVersionNumber });

        // Mark as unmodified since we just saved
        setIsModified(false);

        // Now refetch to sync all data (versions list + recipe content).
        // Tag invalidation from the mutation also triggers background refetch
        // of getTransformerVersions and getRecipeContent.
        await refetch();

        setSnackbar({
          open: true,
          message: `New version v${newVersionNumber} created`,
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: error instanceof Error ? error.message : 'Failed to create new version',
          severity: 'error',
        });
      }
    },
    [
      recipeId,
      appId,
      editorMode,
      yamlContent,
      versionsData,
      state.recipeMetadata,
      state.recipe,
      saveAsNewVersion,
      dispatch,
      setIsModified,
      refetch,
    ]
  );

  const handleDeploy = useCallback(async () => {
    if (!recipeId || !appId || !selectedEnvironment || !versionsData) return;

    const currentVer = versionsData.find((v) => String(v.version) === String(currentVersion));
    if (!currentVer) return;

    try {
      await deployTransformer({
        appId,
        baseId: recipeId,
        envId: selectedEnvironment,
        transId: currentVer.id,
      }).unwrap();

      setSnackbar({ open: true, message: 'Recipe deployed successfully', severity: 'success' });
    } catch (error) {
      logger.error('Failed to deploy recipe:', error);
      setSnackbar({ open: true, message: 'Failed to deploy recipe', severity: 'error' });
    }
  }, [recipeId, appId, selectedEnvironment, deployTransformer, versionsData, currentVersion]);

  const handleVersionSelect = useCallback(
    (version: number) => {
      if (!versionsData) return;

      const versionNum = typeof version === 'number' ? version : parseInt(String(version), 10);
      const selectedVersion = versionsData.find(
        (v) => parseInt(String(v.version), 10) === versionNum
      );
      if (!selectedVersion) return;

      setRecipeLoading(true);

      let recipe: Recipe | null = null;
      const configJson = selectedVersion.configJson || '';
      if (configJson) {
        try {
          const parsed = JSON.parse(configJson);
          recipe = parsed.recipe || parsed;
        } catch {
          /* config parse failed, use YAML only */
        }
      }

      const yamlToSet = selectedVersion.config || configJson || '';

      if (recipe) {
        setRecipe(recipe);
        const { nodes: newNodes, edges: newEdges } = recipeToNodes(recipe);
        setNodes(newNodes);
        setEdges(newEdges);
      } else {
        setRecipe({});
        setNodes([]);
        setEdges([]);
      }
      pushYamlToEditor(yamlToSet);
      setIsModified(false);

      dispatch({ type: 'INCREMENT_REACT_FLOW_KEY' });
      dispatch({ type: 'SET_CURRENT_VERSION', payload: versionNum });

      // Determine read-only state: deployed OR non-latest → read-only
      // Matches legacy edittrans.js: readOnly = !curVersionIsLatest || versionIsDeployed
      const sortedVersions = [...versionsData].sort((a, b) => {
        const vA = parseInt(String(a.version), 10) || 0;
        const vB = parseInt(String(b.version), 10) || 0;
        return vB - vA;
      });
      const latestVersionNum = sortedVersions[0]
        ? parseInt(String(sortedVersions[0].version), 10)
        : 0;
      const isLatest = versionNum === latestVersionNum;
      const isDeployed =
        !!selectedVersion.deployed ||
        (deployments || []).some((d) => d.transId === selectedVersion.id);

      dispatch({ type: 'SET_IS_LATEST_VERSION', payload: isLatest });
      dispatch({ type: 'SET_VERSION_IS_DEPLOYED', payload: isDeployed });

      if (!isLatest || isDeployed) {
        dispatch({ type: 'SET_IS_READ_ONLY', payload: true });
        dispatch({ type: 'SET_AUTOSAVE_ENABLED', payload: false });
      } else {
        dispatch({ type: 'SET_IS_READ_ONLY', payload: false });
      }

      dispatch({
        type: 'SET_RECIPE_METADATA',
        payload: {
          versionId: String(selectedVersion.id),
          name: selectedVersion.name || '',
          config: selectedVersion.config || '',
          environ: selectedVersion.environ || '',
          description: selectedVersion.description || '',
          languageVersion: selectedVersion.languageVersion || '2.0',
          processingType: selectedVersion.processingType || 'batch',
          maxDataProcTime: selectedVersion.maxDataProcTime || '00:00:00',
          templateName: selectedVersion.templateName || '',
        },
      });

      sessionStorage.setItem('CURRENT_RECIPE_FROM_NETWORK', JSON.stringify(recipe || {}));
      sessionStorage.removeItem('UPDATED_RECIPE');

      emitRecipeLoaded();
      dispatch({ type: 'SET_IS_SWITCHING_RECIPE', payload: false });
    },
    [
      versionsData,
      deployments,
      dispatch,
      setRecipe,
      setNodes,
      setEdges,
      pushYamlToEditor,
      setIsModified,
    ]
  );

  // ========================================
  // Load specific version from URL param (?version=N)
  // ========================================
  // Used when deep-linking from the Schedules page so the editor opens the
  // exact version associated with the schedule (matches legacy navigation
  // intent of "open the scheduled recipe").
  //
  // Flow:
  //   1. URL contains ?version=N.
  //   2. Recipe content loads → currentVersion is set to the LATEST version.
  //   3. versionsData loads → contains the full config for every version.
  //   4. This effect detects the divergence and switches to version N once,
  //      then strips the param so the user can switch freely afterwards.
  //
  // The single-shot ref keys on `recipeId` so navigating between recipes (or
  // re-opening the same recipe from a different schedule with a different
  // version) re-triggers the switch.
  const [searchParams, setSearchParams] = useSearchParams();
  const versionParamAppliedForRecipeRef = useRef<string | null>(null);

  useEffect(() => {
    const versionParam = searchParams.get('version');
    if (!versionParam || !recipeId) return;
    if (versionParamAppliedForRecipeRef.current === recipeId) return;
    if (!versionsData || versionsData.length === 0) return;
    // Wait until the initial recipe content load has populated currentVersion;
    // otherwise handleVersionSelect's read-only logic runs against stale state.
    if (currentVersion == null) return;

    const targetVersion = parseInt(versionParam, 10);
    if (Number.isNaN(targetVersion)) return;

    const exists = versionsData.some((v) => parseInt(String(v.version), 10) === targetVersion);
    if (!exists) {
      // Version no longer exists (deleted?). Leave the editor on its default
      // (latest) version and clear the param so we don't keep retrying.
      const next = new URLSearchParams(searchParams);
      next.delete('version');
      setSearchParams(next, { replace: true });
      versionParamAppliedForRecipeRef.current = recipeId;
      return;
    }

    versionParamAppliedForRecipeRef.current = recipeId;

    if (parseInt(String(currentVersion), 10) !== targetVersion) {
      handleVersionSelect(targetVersion);
    }

    // Strip the param after applying so the URL stays clean and the user can
    // freely switch versions via the toolbar without us "snapping back".
    const next = new URLSearchParams(searchParams);
    next.delete('version');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, recipeId, versionsData, currentVersion, handleVersionSelect]);

  const handleSaveSqlQuery = useCallback(() => {
    // TODO: Update the YAML content with the new SQL query
    setSnackbar({ open: true, message: 'SQL query updated', severity: 'success' });
  }, []);

  // Command palette commands
  const commands = useCommandPaletteCommands({
    undo: toolbarActions.handleUndo,
    redo: toolbarActions.handleRedo,
    save: toolbarActions.handleSave,
    toggleAutosave: toolbarActions.toggleAutosave,
    export: toolbarActions.handleExport,
    comment: toolbarActions.toggleComment,
    format: toolbarActions.formatDocument,
    foldSchemas: toolbarActions.toggleSchemaFold,
    runAsJob: () => toolbarActions.handleRun('runAsJob'),
    testRecipe: () => toolbarActions.handleRun('runAsEvent'),
    viewExecutionLog: toolbarActions.toggleExecutionLog,
    viewVersions: toolbarActions.openVersionsDialog,
    createNewVersion: toolbarActions.openCheckInDialog,
    compareVersions: toolbarActions.toggleCompare,
    deploy: toolbarActions.handleDeploy,
    schedule: toolbarActions.openScheduleDialog,
    metadata: toolbarActions.toggleMetadata,
    templates: () => {}, // TODO
    toggleMinimap: toolbarActions.toggleMinimap,
    toggleSecondaryToolbar: () => dispatch({ type: 'TOGGLE_SECONDARY_TOOLBAR' }),
    switchEditorMode: () =>
      toolbarActions.handleEditorModeChange(editorMode === 'visual' ? 'yaml' : 'visual'),
    editSqlQuery: toolbarActions.openSqlEditor,
  });

  // Keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 'k',
      cmd: true,
      handler: toolbarActions.openCommandPalette,
      description: 'Command Palette',
    },
    { key: 's', cmd: true, handler: toolbarActions.handleSave, description: 'Save' },
    { key: 'z', cmd: true, handler: toolbarActions.handleUndo, description: 'Undo' },
    { key: 'z', cmd: true, shift: true, handler: toolbarActions.handleRedo, description: 'Redo' },
    { key: '/', cmd: true, handler: toolbarActions.toggleComment, description: 'Comment' },
  ]);

  // Autosave functionality - only for YAML mode, disabled for deployed/old versions
  useEffect(() => {
    // Disable autosave in read-only mode (deployed or old versions), visual mode, or when not modified
    if (
      !state.autosaveEnabled ||
      !state.isModified ||
      state.isSaving ||
      isReadOnly ||
      editorMode !== 'yaml'
    ) {
      return;
    }

    // Debounce autosave - wait 3 seconds after last change
    const autosaveTimer = setTimeout(() => {
      //
      toolbarActions.handleSave();
    }, 3000);

    return () => clearTimeout(autosaveTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toolbarActions is stable from custom hook
  }, [
    state.autosaveEnabled,
    state.isModified,
    state.isSaving,
    isReadOnly,
    editorMode,
    toolbarActions.handleSave,
  ]);

  // Handle YAML changes — updates context state and marks as modified.
  //
  // This callback fires for ALL Monaco content changes, including both user
  // edits (typing, pasting) and imperative pushes (editor.setValue() from
  // pushYamlToEditor). We use isPushingToEditorRef to distinguish the two:
  //   - Imperative push (isPushingToEditorRef === true): skip modification
  //   - User edit (isPushingToEditorRef === false): mark as modified directly
  //
  // We call setIsModified(true) directly instead of emitRecipeModified() to
  // bypass the event system's ignoreNextModifications counter, which is designed
  // to suppress spurious ReactFlow events after recipe load. That counter
  // consumed paste events (a single event vs. many keystrokes) causing the save
  // button to stay disabled after pasting content.
  const handleYamlChange = useCallback(
    (value: string) => {
      setYamlContent(value);
      if (!isPushingToEditorRef.current) {
        setIsModified(true);
      }
    },
    [setYamlContent, setIsModified]
  );

  // Stable callback for YamlEditor onEditorMount — avoids inline arrow function
  // which would create a new reference every render and cause handleEditorDidMount
  // inside YamlEditor to be recreated (even though the library stores onMount in
  // a ref, the memoization chain is still broken).
  const handleEditorMount = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Monaco editor type
    (editor: any) => {
      yamlEditorRef.current = editor;
      toolbarActions.setEditorRef(editor);
    },
    [toolbarActions]
  );

  // Stable callbacks for YamlEditor's debounced analysis results.
  // These replace the context subscription inside YamlEditor.
  const handleCanUndoChange = useCallback(
    (canUndo: boolean) => dispatch({ type: 'SET_CAN_UNDO', payload: canUndo }),
    [dispatch]
  );
  const handleCanRedoChange = useCallback(
    (canRedo: boolean) => dispatch({ type: 'SET_CAN_REDO', payload: canRedo }),
    [dispatch]
  );
  const handleShowSchemaButtonChange = useCallback(
    (show: boolean) => dispatch({ type: 'SET_SHOW_SCHEMA_BUTTON', payload: show }),
    [dispatch]
  );
  const handleSqlQueryChange = useCallback(
    (query: string | null) => dispatch({ type: 'SET_SELECTED_SQL_QUERY', payload: query }),
    [dispatch]
  );

  // Show full-page loading skeleton ONLY on initial load (no cached data, not a recipe switch).
  // During recipe switches, the editor overlay handles the loading UX so the user
  // keeps visual context (toolbar, sidebar) instead of a jarring full-page skeleton.
  if (isLoadingRecipe && !state.isSwitchingRecipe) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw' }}>
        <MuiToolbar variant="dense">
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={200} height={32} />
        </MuiToolbar>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={48} />
        </Box>
      </Box>
    );
  }

  // Show error state
  if (loadError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw' }}>
        <MuiToolbar variant="dense">
          <Skeleton variant="text" width={200} height={32} />
        </MuiToolbar>
        <Box
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}
        >
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            Failed to load recipe. Please try again.
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <RecipeEditorToolbar
        onUndo={toolbarActions.handleUndo}
        onRedo={toolbarActions.handleRedo}
        onSave={toolbarActions.handleSave}
        onSaveMenuOpen={toolbarActions.handleSaveMenuOpen}
        onDiscardChanges={toolbarActions.handleDiscardChanges}
        onRun={toolbarActions.handleRun}
        onRunMenuOpen={toolbarActions.handleRunMenuOpen}
        onDeploy={toolbarActions.handleDeploy}
        onVersionsClick={toolbarActions.openVersionsDialog}
        onCheckInClick={toolbarActions.openCheckInDialog}
        onToolsMenuOpen={toolbarActions.handleToolsMenuOpen}
        onScheduleClick={toolbarActions.openScheduleDialog}
        onCommandPaletteClick={toolbarActions.openCommandPalette}
        onEditorModeChange={toolbarActions.handleEditorModeChange}
        onExecutionLogToggle={toolbarActions.toggleExecutionLog}
        onCompareToggle={toolbarActions.toggleCompare}
        onMetadataToggle={toolbarActions.toggleMetadata}
        onEditMenuOpen={toolbarActions.handleEditMenuOpen}
        onViewMenuOpen={toolbarActions.handleViewMenuOpen}
        onToggleComment={toolbarActions.toggleComment}
        onFormatDocument={toolbarActions.formatDocument}
        onFoldSchemas={toolbarActions.toggleSchemaFold}
        onToggleMinimap={toolbarActions.toggleMinimap}
        onOpenSqlEditor={toolbarActions.openSqlEditor}
        onEnvironmentChange={toolbarActions.handleEnvironmentChange}
        onToggleAutosave={toolbarActions.toggleAutosave}
        onSwapJobType={toolbarActions.swapJobType}
        saveMenuAnchor={toolbarActions.saveMenuAnchor}
        runMenuAnchor={toolbarActions.runMenuAnchor}
        toolsMenuAnchor={toolbarActions.toolsMenuAnchor}
        templateMenuAnchor={toolbarActions.templateMenuAnchor}
        editMenuAnchor={toolbarActions.editMenuAnchor}
        viewMenuAnchor={toolbarActions.viewMenuAnchor}
        environments={environmentsList}
        isDeployedToSelectedEnv={isDeployedToSelectedEnv}
      />

      {/* Read-Only Banner - Deployed or Old Version (matches legacy dataProtect) */}
      {isReadOnly && !state.showCompare && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 0.5,
            px: 2,
            bgcolor: state.versionIsDeployed ? '#e5f5ff' : '#fff3e0',
            borderBottom: '1px solid',
            borderColor: state.versionIsDeployed ? '#b3d9f2' : '#ffe0b2',
            flexShrink: 0,
          }}
        >
          {state.versionIsDeployed ? (
            <LockIcon sx={{ fontSize: 18, color: '#1976d2' }} />
          ) : (
            <InfoIcon sx={{ fontSize: 18, color: '#e65100' }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {state.versionIsDeployed
              ? state.isLatestVersion
                ? 'Deployed \u2013 Create a new version to edit.'
                : 'Deployed \u2013 Select the latest version to edit.'
              : 'Read-Only \u2014 This is an old version.'}
          </Typography>
        </Box>
      )}

      {/* Main Content Area - Recipe List + Editor */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {/* Left: Recipe List Sidebar */}
        <RecipeListSidebar />

        {/* Center/Right: Editor Area (with optional Compare split) */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            overflow: 'hidden',
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {/* Main Editor */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              minWidth: 0,
              position: 'relative',
            }}
          >
            {editorMode === 'visual' ? (
              <FlowWithProvider key={`flow-${currentVersion}-${editorMode}`} />
            ) : (
              <YamlEditor
                key={`yaml-${recipeId}`}
                value={yamlContent}
                onChange={handleYamlChange}
                isReadOnly={isReadOnly}
                showMinimap={state.showMinimap}
                onEditorMount={handleEditorMount}
                onCanUndoChange={handleCanUndoChange}
                onCanRedoChange={handleCanRedoChange}
                onShowSchemaButtonChange={handleShowSchemaButtonChange}
                onSqlQueryChange={handleSqlQueryChange}
              />
            )}

            {/* Loading overlay during switching or fetching new recipe */}
            {(state.isSwitchingMode || state.isSwitchingRecipe || isLoadingRecipe) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(4px)',
                  zIndex: 1000,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
          </Box>

          {/* Right: Compare View (when enabled) */}
          {showCompare && (
            <CompareView
              onClose={() => dispatch({ type: 'SET_SHOW_COMPARE', payload: false })}
              versions={versions}
              currentVersion={currentVersion}
            />
          )}
        </Box>
      </Box>

      {/* Bottom: Tabbed panel (Recipe Running, Event Context, Execution Log) */}
      {bottomPanelTabs.length > 0 && (
        <BottomPanel
          tenantId={tenantId}
          environments={environmentsList}
          tenantKey={tenantData?.key || ''}
          appUniqueKey={app.uniqueKey || ''}
        />
      )}

      {/* Command Palette (Cmd+K) */}
      <CommandPalette
        open={showCommandPalette}
        onClose={() => dispatch({ type: 'SET_SHOW_COMMAND_PALETTE', payload: false })}
        commands={commands}
      />

      {/* All Modals */}
      <VersionsDialog
        open={showVersionsDialog}
        onClose={() => dispatch({ type: 'SET_SHOW_VERSIONS_DIALOG', payload: false })}
        versions={versions}
        currentVersion={currentVersion}
        onVersionSelect={handleVersionSelect}
        isLoading={isLoadingVersions}
      />

      <CheckInDialog
        open={showCheckInDialog}
        onClose={() => dispatch({ type: 'SET_SHOW_CHECKIN_DIALOG', payload: false })}
        onCreateVersion={handleCreateVersion}
      />

      <DeployDialog
        open={showDeployDialog}
        onClose={() => dispatch({ type: 'SET_SHOW_DEPLOY_DIALOG', payload: false })}
        onDeploy={handleDeploy}
        environmentName={
          environmentsList.find((e) => e.id === selectedEnvironment)?.name || 'Unknown'
        }
      />

      <ScheduleDialog
        open={showScheduleDialog}
        onClose={() => dispatch({ type: 'SET_SHOW_SCHEDULE_DIALOG', payload: false })}
        recipeBaseId={recipeId || ''}
        tenantId={tenantId}
        showTemplateSelection={state.showTemplateSelection}
      />

      <TemplateDialog
        open={showTemplateDialog}
        onClose={() => dispatch({ type: 'SET_SHOW_TEMPLATE_DIALOG', payload: false })}
        selectedTemplate={state.selectedTemplate}
        onTemplateChange={(template) =>
          dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: template })
        }
      />

      <MetadataDialog
        open={showMetadataDialog}
        onClose={() => dispatch({ type: 'SET_SHOW_METADATA_DIALOG', payload: false })}
        environmentName={
          environmentsList.find((e) => e.id === selectedEnvironment)?.name || 'Unknown'
        }
        metadata={null} // TODO: fetch metadata
      />

      <SqlEditorDialog
        open={showSqlEditorDialog}
        onClose={() => dispatch({ type: 'SET_SHOW_SQL_EDITOR_DIALOG', payload: false })}
        onSave={handleSaveSqlQuery}
        initialSql={selectedSqlQuery || ''}
      />

      <SaveConflictDialog
        open={showSaveConflictDialog}
        onCancel={() => dispatch({ type: 'SET_SHOW_SAVE_CONFLICT_DIALOG', payload: false })}
        onOverwrite={toolbarActions.handleSave}
      />

      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onSave={toolbarActions.handleSave}
        onDiscard={() => {
          dispatch({ type: 'SET_SHOW_UNSAVED_CHANGES_DIALOG', payload: false });
          setIsModified(false);
        }}
        onCancel={() => dispatch({ type: 'SET_SHOW_UNSAVED_CHANGES_DIALOG', payload: false })}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/**
 * Recipe Editor - Main component with providers
 */
export const RecipeEditor = () => {
  const { recipeId } = useParams<{ recipeId: string }>();

  return (
    <RecipeEditorProvider>
      <ReactFlowProvider>
        <RecipeEditorContent recipeId={recipeId} />
      </ReactFlowProvider>
    </RecipeEditorProvider>
  );
};
